// src/components/ProductCard.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faClock } from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../context/CartContext';
import { useStock } from '../context/StockContext';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../context/LocaleContext';

const MAX_QUANTITY_PER_ITEM = 5;

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  image: string;
  quantity: number;
  oneOfKind?: boolean;
  slug: string;
  variantId?: string | null;
  onQuantityChange?: (quantity: number) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  name,
  price,
  image,
  quantity,
  oneOfKind,
  slug,
  variantId,
  onQuantityChange
}) => {
  const { updateQuantity, removeItem } = useCart();
  const { getStock, isMadeToOrder } = useStock();
  const { t } = useTranslation();
  const { localePath } = useLocale();
  const [localQuantity, setLocalQuantity] = useState(quantity);
  const removalTimeout = useRef<NodeJS.Timeout>();
  const availableStock = getStock(id, variantId || undefined);
  const madeToOrder = isMadeToOrder(id, variantId || undefined);
  const isMadeToOrderItem = availableStock === 0 && madeToOrder;
  
  const handleQuantityChange = (newQuantity: number) => {
    // Enforce max quantity limit for all items
    if (newQuantity > MAX_QUANTITY_PER_ITEM) {
      return;
    }

    // For made-to-order items, only enforce max limit (not stock limit)
    if (!isMadeToOrderItem && newQuantity > availableStock) {
      return;
    }

    const validQuantity = Math.max(0, newQuantity);
    setLocalQuantity(validQuantity);
    
    if (validQuantity === 0) {
      if (removalTimeout.current) {
        clearTimeout(removalTimeout.current);
      }
      removalTimeout.current = setTimeout(() => {
        removeItem(id);
      }, 800);
    } else {
      if (removalTimeout.current) {
        clearTimeout(removalTimeout.current);
      }
      if (onQuantityChange) {
        onQuantityChange(validQuantity);
      } else {
        updateQuantity(id, validQuantity);
      }
    }
  };

  // Update local quantity when prop changes
  useEffect(() => {
    setLocalQuantity(quantity);
  }, [quantity]);

  const QuantitySelector = () => {
    const maxAllowed = isMadeToOrderItem ? MAX_QUANTITY_PER_ITEM : Math.min(availableStock, MAX_QUANTITY_PER_ITEM);
    const atMaxQuantity = localQuantity >= maxAllowed;

    return (
      <div className="flex items-center gap-2 quantity-controls">
        <button
          onClick={() => handleQuantityChange(localQuantity - 1)}
          className="px-2 py-1 bg-gray-200 dark:bg-brand-charcoal rounded"
          disabled={localQuantity <= 0}
        >
          -
        </button>
        <span className="text-black dark:text-white">
          {localQuantity}
        </span>
        <button
          onClick={() => handleQuantityChange(localQuantity + 1)}
          className="px-2 py-1 bg-gray-200 dark:bg-brand-charcoal rounded"
          disabled={atMaxQuantity}
        >
          +
        </button>
        {atMaxQuantity && (
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
            {localQuantity >= MAX_QUANTITY_PER_ITEM ? t('cart.maxPerItem') : t('cart.maxStock')}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="flex items-center gap-4 p-4 bg-white dark:bg-brand-dark rounded-none md:rounded-xl shadow-none md:shadow-sm">
      <div className="relative flex-shrink-0">
        <Link
          to={localePath('/product/:slug', { slug })}
          className="product-link hover:opacity-75 transition-opacity"
        >
          <img src={image} alt={name} className="w-32 h-32 md:w-20 md:h-20 object-cover rounded-none md:rounded-lg" />
          {oneOfKind && (
            <div className="absolute top-2 right-2 bg-brand-blue/80 backdrop-blur-sm text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
              <FontAwesomeIcon icon={faStar} className="h-3 w-3" />
            </div>
          )}
        </Link>
      </div>
      <div className="flex-grow">
        <Link
          to={localePath('/product/:slug', { slug })}
          className="product-link hover:text-brand-blue transition-colors"
        >
          <h3 className="text-lg font-semibold text-black dark:text-white">{name}</h3>
        </Link>
        {isMadeToOrderItem && (
          <div className="flex items-center gap-1.5 mt-1 text-amber-600 dark:text-amber-500 text-sm">
            <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
            <span>{t('cart.madeToOrderBadge')}</span>
          </div>
        )}
        <div className="flex items-center justify-between mt-2">
          <div className="space-y-2">
            <p className="text-black dark:text-white">€{price.toFixed(2)}</p>
            {!oneOfKind && (
              <QuantitySelector />
            )}
          </div>
          <button
            onClick={() => removeItem(id)}
            className="remove-button text-red-500 hover:text-red-700 active:text-red-800 transition-colors"
          >
            {t('cart.remove')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;